<script setup lang="ts">
import { computed } from 'vue';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { useMutation } from '@tanstack/vue-query';
import { useAuditUser } from '@/composables/useAuditUser';
import { caregiversApi } from '@/api/audit/caregivers';
import type { AxiosError } from 'axios';

const ONE_SECOND_IN_MS = 1_000;

const { t } = useI18n();

const { auditUser, setCurrentAuditUser } = useAuditUser();

defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const validationSchema = yup.object({
  email: yup
    .string()
    .email(t('auditLogin.emailInvalid'))
    .required(t('auditLogin.emailRequired'))
    .test(
      'different-email',
      t('auditCaregiverChangeEmail.emailDifferent'),
      (value) => {
        const currentEmail = (auditUser?.value?.displayEmail || '').toLowerCase().trim();

        return value.toLowerCase().trim() !== currentEmail;
      },
    ),
});

const { handleSubmit, values, errors, defineField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    email: '',
  },
});

const [email, emailAttrs] = defineField('email');

const { mutate: updateEmail, isPending, isError, error, isSuccess, reset } = useMutation<unknown, AxiosError>({
  mutationFn: () => caregiversApi.update(auditUser!.value.auditableId, values.email),
  onSuccess: () => {
    if (auditUser?.value && setCurrentAuditUser) {
      setCurrentAuditUser({
        ...auditUser.value,
        displayEmail: values.email,
      });
    }
  },
});

const onSubmit = handleSubmit(() => {
  updateEmail();
});

function closeDialog() {
  emit('update:modelValue', false);
  setTimeout(() => {
    resetForm();
    reset();
  }, ONE_SECOND_IN_MS);
}

const errorMessage = computed(() => {
  if (isError.value && error.value?.response?.data) {
    const responseData = error.value.response.data as { type?: string };
    if (responseData.type === 'ActiveRecord::RecordNotUnique') {
      return t('caregiverForm.recordNotUnique');
    }

    return t('userSession.defaultError');
  }

  return '';
});
</script>
<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500px"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h5">
        {{ t('auditCaregiverChangeEmail.title') }}
      </v-card-title>
      <v-card-text>
        <p class="mb-4 text-body-2 text-medium-emphasis">
          {{ t('auditCaregiverChangeEmail.currentEmail') }}: <strong>{{ auditUser?.displayEmail }}</strong>
        </p>
        <v-form @submit.prevent="onSubmit">
          <v-text-field
            v-model="email"
            v-bind="emailAttrs"
            variant="outlined"
            :label="t('auditCaregiverChangeEmail.newEmail')"
            type="email"
            :error-messages="errors.email"
            :disabled="isPending || isSuccess"
            autofocus
          />
        </v-form>
        <v-alert
          v-if="isSuccess"
          class="mt-4"
          type="success"
        >
          {{ t('auditCaregiverChangeEmail.success') }}
        </v-alert>
        <v-alert
          v-if="isError && errorMessage"
          class="mt-4"
          type="error"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="isPending"
          @click="closeDialog"
        >
          {{ t('auditCaregiverChangeEmail.close') }}
        </v-btn>
        <v-btn
          v-if="!isSuccess"
          color="primary"
          :loading="isPending"
          @click="onSubmit"
        >
          {{ t('auditCaregiverChangeEmail.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
